import React from 'react';
import { Box, Button, Grid, TextField, FormControlLabel, Checkbox } from '@material-ui/core';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { Alert } from '@mui/material';

import LocationObject from 'entities/locationObject/model';
import { isEin, isEmail, isLocationObject } from 'utils/validators';
import { FormLocationObjectField } from 'retired/FormLocationObjectField/FormLocationObjectField';
import DialogButton from 'retired/DialogButton';
import {
  ChapterRequestQueryResult,
  NonprofitIdentifierType,
  RoleCode,
  useAcceptChapterRequestMutation,
} from 'generated/graphql';

import NonprofitsList from '../NonprofitsList';

interface ChapterRequestFormData {
  parentName: string;
  name: string;
  mailingAddress?: LocationObject;
  emailAddress: string;
  ein: string;
  adminEmail: string;
  adminFirstName: string;
  adminLastName: string;
  addAdditionalAdmin: boolean;
  requesterPreferredName: string;
  requestedByName: string;
  requestedRoles: string;
}

const ChapterRequestForm = ({
  chapterRequest,
  onReject,
}: {
  chapterRequest: ChapterRequestQueryResult;
  onReject: () => void;
}) => {
  const {
    id,
    parentNonprofitId,
    nonprofitName,
    locationObject,
    users,
    email,
    identifiers,
    requestedByName,
    parentName,
    zendeskTicketId,
  } = chapterRequest;

  const emptyUser = {
    roles: [],
    email: '',
    fullName: {
      first: '',
      last: '',
    },
  };

  const user = users.length > 0 ? users[0] : emptyUser;

  const { fullName, email: adminEmail, roles } = user;
  const ein = identifiers[0].id;

  const {
    control,
    formState: { errors, touchedFields },
    getValues,
    watch,
  } = useForm<ChapterRequestFormData>({
    defaultValues: {
      parentName: `${parentName} (${parentNonprofitId})`,
      name: nonprofitName,
      mailingAddress: {
        ...locationObject,
        __typename: undefined,
        geo: { ...locationObject.geo, __typename: undefined },
      },
      emailAddress: email,
      ein,
      adminEmail,
      adminFirstName: fullName.first,
      adminLastName: fullName.last,
      requesterPreferredName: requestedByName,
      requestedRoles: roles.join(', '),
      addAdditionalAdmin: users.length > 0,
    },
    mode: 'onChange',
  });

  const history = useHistory();
  const [acceptChapterRequest, { loading: accepting }] = useAcceptChapterRequestMutation();

  const rejectButton = (
    <Button
      color="primary"
      type="button"
      variant="outlined"
      style={{ marginRight: 8 }}
      onClick={onReject}
    >
      Reject
    </Button>
  );

  const acceptButton = (
    <DialogButton
      title={`Are you sure you want to accept "${nonprofitName}" chapter request?`}
      text="Once confirmed it will create a new nonprofit."
      confirmLabel="Confirm"
      cancelLabel="Cancel"
      buttonProps={{ variant: 'contained', disabled: accepting }}
      handleConfirm={async () => {
        const formValues = getValues();
        const {
          emailAddress,
          ein: formValuesEin,
          mailingAddress,
          name,
          adminEmail: formValuesAdminEmail,
          adminFirstName,
          adminLastName,
          addAdditionalAdmin,
        } = formValues;

        await acceptChapterRequest({
          variables: {
            id,
            hash: '',
            input: {
              email: emailAddress,
              identifiers: [{ type: NonprofitIdentifierType.Ein, id: formValuesEin }],
              locationObject: mailingAddress,
              nonprofitName: name,
              users: addAdditionalAdmin
                ? [
                    {
                      email: formValuesAdminEmail,
                      roles: [RoleCode.Verified, RoleCode.Administrator, RoleCode.Owner],
                      fullName: {
                        first: adminFirstName,
                        last: adminLastName,
                      },
                    },
                  ]
                : [],
            },
          },
          onCompleted: () => history.goBack(),
        });
      }}
    >
      Accept
    </DialogButton>
  );

  return (
    <form>
      <fieldset>
        <Box style={{ paddingTop: 0, marginBottom: 30 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <h2>
              Review chapter request{' '}
              <a
                href={`https://joindeed.zendesk.com/agent/tickets/${zendeskTicketId}`}
                target="_blank"
              >
                #{zendeskTicketId}
              </a>
            </h2>
          </div>
          <Grid container spacing={4}>
            <Grid
              item
              sm={12}
              lg={6}
              style={{ display: 'flex', gap: '24px', flexDirection: 'column' }}
            >
              <Controller
                name="parentName"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Parent organization" fullWidth required disabled />
                )}
              />

              <Controller
                name="name"
                control={control}
                rules={{
                  required: 'This field is required',
                  maxLength: { value: 100, message: 'Please use less than 100 characters' },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Chapter name"
                    fullWidth
                    required
                    error={Boolean(errors.name)}
                    helperText={touchedFields.name && errors.name?.message}
                  />
                )}
              />

              <div>
                <Controller
                  name="mailingAddress"
                  control={control}
                  rules={{
                    required: 'This field is required',
                    validate: isLocationObject(),
                  }}
                  render={({ field }) => (
                    <FormLocationObjectField
                      input={field}
                      meta={{
                        error: Boolean(errors.mailingAddress),
                        touched: Boolean(touchedFields.mailingAddress),
                      }}
                      label="Mailing address"
                      sx={{ textTransform: 'uppercase' }}
                      fullWidth
                      afterSelect={() => {}}
                      helperText={touchedFields.mailingAddress && errors.mailingAddress?.message}
                    />
                  )}
                />
              </div>

              <Controller
                name="emailAddress"
                control={control}
                rules={{
                  required: 'This field is required',
                  validate: isEmail(),
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Chapter Email address"
                    fullWidth
                    required
                    error={Boolean(errors.emailAddress)}
                    helperText={touchedFields.emailAddress && errors.emailAddress?.message}
                  />
                )}
              />

              <Controller
                name="ein"
                control={control}
                rules={{
                  required: 'This field is required',
                  validate: isEin(),
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Chapter EIN / tax ID"
                    fullWidth
                    required
                    error={Boolean(errors.ein)}
                    helperText={touchedFields.ein && errors.ein?.message}
                  />
                )}
              />

              <Box>
                <Alert severity="info" style={{ marginBottom: '16px' }}>
                  All owners for the parent organization will automatically be added to the chapter.
                </Alert>

                <Controller
                  name="addAdditionalAdmin"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={value}
                          onChange={(event) => {
                            onChange(event.target.checked);
                          }}
                        />
                      }
                      label="Add an additional user as an owner"
                    />
                  )}
                />
              </Box>

              {watch('addAdditionalAdmin') && (
                <>
                  <Controller
                    name="adminEmail"
                    control={control}
                    rules={{
                      required: 'This field is required',
                      validate: isEmail(),
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Chapter admin email address"
                        fullWidth
                        required
                        error={Boolean(errors.adminEmail)}
                        helperText={
                          (touchedFields.emailAddress && errors.emailAddress?.message) ||
                          'Tip: By default, all active Owners for the parent organization will automatically be added as Owners for any chapters created. (if you share an EIN)'
                        }
                      />
                    )}
                  />
                  <Controller
                    name="adminFirstName"
                    control={control}
                    rules={{
                      required: 'This field is required',
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Chapter admin first name"
                        fullWidth
                        required
                        error={Boolean(errors.adminFirstName)}
                        helperText={touchedFields.adminFirstName && errors.adminFirstName?.message}
                      />
                    )}
                  />

                  <Controller
                    name="adminLastName"
                    control={control}
                    rules={{
                      required: 'This field is required',
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Chapter admin last name"
                        fullWidth
                        required
                        error={Boolean(errors.adminLastName)}
                        helperText={touchedFields.adminLastName && errors.adminLastName?.message}
                      />
                    )}
                  />
                </>
              )}

              <Controller
                name="requesterPreferredName"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Requested by" fullWidth disabled />
                )}
              />

              <Controller
                name="requestedRoles"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Requested roles" fullWidth disabled />
                )}
              />
            </Grid>
            <Grid item sm={12} lg={6}>
              <NonprofitsList nonprofitName={nonprofitName} />
            </Grid>
          </Grid>
          <div style={{ marginTop: 30, display: 'flex', justifyContent: 'end' }}>
            {rejectButton}

            {acceptButton}
          </div>
        </Box>
      </fieldset>
    </form>
  );
};

export default ChapterRequestForm;
